import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  Inbox, 
  FolderKanban, 
  Rocket,
  CheckCircle2, 
  ArrowRight,
  Brain,
  Calendar,
  Bell,
  Repeat,
  FileBox,
  Target,
  Briefcase,
  Users,
  Baby,
  Heart,
  Zap,
  ChevronDown,
  ChevronUp,
  Sparkles
} from "lucide-react";
import { Helmet } from "react-helmet";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { WaitlistForm } from "@/components/marketing/WaitlistForm";
import { AnimatedSection, StaggerContainer } from "@/components/marketing/AnimatedSection";
import { getAuthUrl } from "@/lib/authUrl";
import { useState } from "react";

const whatIsSerentoCards = [
  {
    icon: FolderKanban,
    title: "Life Admin Focus",
    description: "Built for the messy reality of bills, renewals, appointments, and recurring responsibilities."
  },
  {
    icon: Sparkles,
    title: "Automatic Organization",
    description: "Tasks are categorized, prioritized, and scheduled without manual effort."
  },
  {
    icon: Brain,
    title: "Calm Execution System",
    description: "A trusted system that gives you clarity, not more overwhelm."
  }
];

const whoItsFor = [
  { icon: Briefcase, label: "Busy professionals" },
  { icon: Baby, label: "Parents" },
  { icon: Zap, label: "Entrepreneurs" },
  { icon: Heart, label: "Overwhelmed individuals" },
  { icon: Brain, label: "ADHD-friendly users" }
];

const howItWorksSteps = [
  {
    icon: Inbox,
    title: "Capture Everything",
    description: "Add tasks from voice, email, photos, or manual entry. Works with your real-life inputs."
  },
  {
    icon: FolderKanban,
    title: "Organize Automatically",
    description: "Serento categorizes, prioritizes, and assigns due dates — all without manual sorting."
  },
  {
    icon: Rocket,
    title: "Execute with Clarity",
    description: "Focus on today's priorities. Run a weekly reset. Stay in control without chaos."
  }
];

const benefits = [
  "Reduce stress and mental load",
  "Never forget important tasks",
  "Save hours each week on planning",
  "Stay consistent with recurring responsibilities",
  "One trusted system for your entire life"
];

const features = [
  {
    icon: Inbox,
    title: "Smart Task Capture",
    description: "Voice, email, photos — capture tasks however they come to you."
  },
  {
    icon: FolderKanban,
    title: "Life-Based Categories",
    description: "12 life domains: Health, Home, Money, Family, and more."
  },
  {
    icon: Calendar,
    title: "Weekly Admin Session",
    description: "15 minutes weekly for total clarity and control."
  },
  {
    icon: Target,
    title: "Priority Engine",
    description: "AI-powered prioritization surfaces what matters most today."
  },
  {
    icon: Bell,
    title: "Calm Reminders",
    description: "Gentle nudges at the right time. Never miss what's important."
  },
  {
    icon: Repeat,
    title: "Recurring Life Maintenance",
    description: "Bills, renewals, checkups — handled automatically."
  },
  {
    icon: FileBox,
    title: "Central Life Hub",
    description: "All your life admin in one calm, organized dashboard."
  }
];


const faqs = [
  {
    question: "Is Serento just a to-do list?",
    answer: "No. Serento is a life organizer built for personal life admin — bills, renewals, family coordination, health appointments, and the 150+ recurring responsibilities adults juggle. It's not about checking boxes; it's about achieving mental clarity."
  },
  {
    question: "How long does setup take?",
    answer: "Most users are up and running in under 5 minutes. Serento learns from your inputs over time, so the more you use it, the smarter it gets at organizing your life."
  },
  {
    question: "Is it ADHD-friendly?",
    answer: "Absolutely. Serento is designed with ADHD-friendly principles: minimal friction to capture tasks, calm (not overwhelming) interfaces, gentle reminders, and a weekly reset system that prevents things from falling through the cracks."
  },
  {
    question: "Is there a free plan?",
    answer: "Yes! We offer a free tier with core features including basic task management, the Weekly Admin Session, and organization across all 12 life domains. Premium features like voice capture and AI prioritization are available on paid plans."
  },
  {
    question: "What devices are supported?",
    answer: "Serento installs like an app — no App Store, no updates, works everywhere. It works on any device with a web browser — desktop, tablet, or mobile. Just visit the site and add it to your home screen for a native app experience."
  },
  {
    question: "Do I need to download anything?",
    answer: "No downloads required. Serento installs like an app — no App Store, no updates, works everywhere. Just open it in your browser and add to your home screen. It works offline and feels just like a native app."
  }
];

export default function MarketingHome() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <Helmet>
        <title>Serento — organize your life without relying on memory</title>
        <meta name="description" content="Serento helps you organize tasks, notes, and life admin in one place so you don't rely on memory." />
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="life organizer, life admin app, task manager, organize tasks and deadlines, ADHD productivity, weekly planning" />
        <link rel="canonical" href="https://serento.ai/" />
        <meta property="og:title" content="Serento — organize your life without relying on memory" />
        <meta property="og:description" content="Serento helps you organize tasks, notes, and life admin in one place so you don't rely on memory." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://serento.ai/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Serento",
            "url": "https://serento.ai",
            "logo": "https://serento.ai/serento-logo.png",
            "description": "Serento helps you organize tasks, notes, and life admin in one place so you don't rely on memory.",
            "sameAs": []
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Serento",
            "url": "https://serento.ai",
            "description": "Organize your life without relying on memory"
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://serento.ai/"
            }]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <MarketingHeader />

        <main>
          {/* Hero Section - H1 Zone */}
          <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection animation="fade-up">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-foreground">
                Serento — organize your life without relying on memory
              </h1>
            </AnimatedSection>
            
            <AnimatedSection animation="fade-up" delay={100}>
              <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Serento helps you organize tasks, notes, and life admin in one place so you don't rely on memory.
              </p>
            </AnimatedSection>
            
            <AnimatedSection animation="fade-up" delay={200}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button 
                  size="lg" 
                  className="h-12 px-8 hover-scale"
                  asChild
                >
                  <a href={getAuthUrl()}>
                    Get Started
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="h-12 px-8 hover-scale"
                  onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
                >
                  See How It Works
                </Button>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={300}>
              <div id="hero-form" className="max-w-md mx-auto mb-8">
                <WaitlistForm variant="hero" source="hero" />
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={400}>
              <p className="text-sm text-muted-foreground">
                Serento installs like an app — no App Store, no updates, works everywhere.
              </p>
            </AnimatedSection>
          </div>
        </section>

        {/* What Is Serento Section */}
        <section id="what-is-serento" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                  What Is Serento?
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Serento is a life organization app — not just another to-do list. It's designed for the real complexity of personal life admin.
                </p>
              </div>
            </AnimatedSection>
            
            <div className="grid md:grid-cols-3 gap-6">
              {whatIsSerentoCards.map((card, idx) => (
                <AnimatedSection key={idx} animation="fade-up" delay={idx * 100}>
                  <Card className="p-6 h-full hover:shadow-lg transition-shadow">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <card.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-foreground">{card.title}</h3>
                    <p className="text-muted-foreground">{card.description}</p>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Who It's For Section */}
        <section id="who-its-for" className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                  Who Serento Is For
                </h2>
              </div>
            </AnimatedSection>
            
            <StaggerContainer className="flex flex-wrap justify-center gap-4" staggerDelay={80}>
              {whoItsFor.map((persona, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-3 px-5 py-3 rounded-full bg-muted border border-border hover:border-primary/30 transition-colors"
                >
                  <persona.icon className="w-5 h-5 text-primary" />
                  <span className="text-foreground font-medium">{persona.label}</span>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                  How Serento Works
                </h2>
              </div>
            </AnimatedSection>
            
            <div className="grid md:grid-cols-3 gap-8">
              {howItWorksSteps.map((step, idx) => (
                <AnimatedSection key={idx} animation="fade-up" delay={idx * 150}>
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <step.icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-foreground">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                  Benefits of Using Serento
                </h2>
              </div>
            </AnimatedSection>
            
            <StaggerContainer className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto" staggerDelay={80}>
              {benefits.map((benefit, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-3 p-4 rounded-lg bg-success/5 border border-success/10 hover:border-success/20 transition-colors"
                >
                  <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
                  <span className="text-foreground">{benefit}</span>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                  Core Features
                </h2>
              </div>
            </AnimatedSection>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, idx) => (
                <AnimatedSection key={idx} animation="fade-up" delay={idx * 75}>
                  <Card className="p-6 h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Differentiation Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-8">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                  Why Serento Is Different
                </h2>
              </div>
            </AnimatedSection>
            
            <AnimatedSection animation="fade-up" delay={100}>
              <Card className="p-8 bg-card">
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    <strong className="text-foreground">Unlike to-do lists</strong>, Serento doesn't just store tasks — it organizes them across 12 life domains so nothing falls through the cracks.
                  </p>
                  <p>
                    <strong className="text-foreground">Unlike productivity apps</strong>, Serento is built for personal life, not work projects. Bills, renewals, family schedules, health appointments — the stuff that clutters your brain.
                  </p>
                  <p>
                    <strong className="text-foreground">Unlike work-only tools</strong>, Serento understands that life admin is ongoing and never-ending. Our Weekly Admin Session gives you a reset ritual to stay on top of everything.
                  </p>
                </div>
              </Card>
            </AnimatedSection>
          </div>
        </section>


        {/* FAQ Section */}
        <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                  Frequently Asked Questions
                </h2>
              </div>
            </AnimatedSection>
            
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <AnimatedSection key={idx} animation="fade-up" delay={idx * 50}>
                  <div className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-4 text-left bg-card hover:bg-muted/50 transition-colors"
                    >
                      <span className="font-medium text-foreground pr-4">{faq.question}</span>
                      {openFaq === idx ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      )}
                    </button>
                    {openFaq === idx && (
                      <div className="p-4 pt-0 bg-card">
                        <p className="text-muted-foreground">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-2xl mx-auto text-center">
            <AnimatedSection animation="fade-up">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-foreground">
                Organize Your Life With Clarity
              </h2>
            </AnimatedSection>
            
            <AnimatedSection animation="fade-up" delay={100}>
              <Button 
                size="lg" 
                className="h-12 px-8 hover-scale"
                asChild
              >
                <a href={getAuthUrl()}>
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </AnimatedSection>
          </div>
          </section>
        </main>

        <MarketingFooter />
      </div>
    </>
  );
}
